const STORAGE_KEY = 'dms_themes';
const ACTIVE_KEY = 'dms_active_theme_id';
const THEME_VERSION_KEY = 'dms_theme_version';
const THEME_VERSION = 3;

const DEFAULT_THEME = {
  id: 'default',
  name: 'Default (SEC Bright Green)',
  isActive: true,
  colors: {
    primary: '#039966',
    secondary: '#008b5c',
    navbarBg: '#039966',
    navbarText: '#ffffff',
    fieldBorder: '#E3E6EF',
    fieldBg: '#ffffff',
    bodyBg: '#F8F9FB',
    textPrimary: '#404040',
  },
  fontFamily: 'system',
  buttonRadius: 4,
};

export const PRESETS = [
  {
    key: 'pmo-blue',
    name: 'PMO Blue',
    swatch: '#4169E1',
    colors: {
      primary: '#4169E1',
      secondary: '#2d52b8',
      navbarBg: '#4169E1',
      navbarText: '#ffffff',
      fieldBorder: '#E3E6EF',
      fieldBg: '#ffffff',
      bodyBg: '#F8F9FB',
      textPrimary: '#404040',
    },
    fontFamily: 'system',
    buttonRadius: 4,
  },
  {
    key: 'navy-blue',
    name: 'Navy Blue',
    swatch: '#102566',
    colors: {
      primary: '#102566',
      secondary: '#5840FF',
      navbarBg: '#102566',
      navbarText: '#ffffff',
      fieldBorder: '#E3E6EF',
      fieldBg: '#ffffff',
      bodyBg: '#F8F9FB',
      textPrimary: '#404040',
    },
    fontFamily: 'system',
    buttonRadius: 4,
  },
  {
    key: 'forest-green',
    name: 'Forest Green',
    swatch: '#1D6F52',
    colors: {
      primary: '#1D6F52',
      secondary: '#2ECC8C',
      navbarBg: '#155c41',
      navbarText: '#ffffff',
      fieldBorder: '#C6E8DC',
      fieldBg: '#ffffff',
      bodyBg: '#F4FAF7',
      textPrimary: '#1a3a2e',
    },
    fontFamily: 'system',
    buttonRadius: 6,
  },
  {
    key: 'ocean-blue',
    name: 'Ocean Blue',
    swatch: '#0369A1',
    colors: {
      primary: '#0369A1',
      secondary: '#0EA5E9',
      navbarBg: '#0C4A6E',
      navbarText: '#ffffff',
      fieldBorder: '#BAE6FD',
      fieldBg: '#ffffff',
      bodyBg: '#F0F9FF',
      textPrimary: '#0C4A6E',
    },
    fontFamily: 'system',
    buttonRadius: 4,
  },
  {
    key: 'slate-gray',
    name: 'Slate Gray',
    swatch: '#475569',
    colors: {
      primary: '#475569',
      secondary: '#64748B',
      navbarBg: '#1E293B',
      navbarText: '#F1F5F9',
      fieldBorder: '#CBD5E1',
      fieldBg: '#ffffff',
      bodyBg: '#F8FAFC',
      textPrimary: '#334155',
    },
    fontFamily: 'system',
    buttonRadius: 4,
  },
  {
    key: 'warm-amber',
    name: 'Warm Amber',
    swatch: '#B45309',
    colors: {
      primary: '#B45309',
      secondary: '#F59E0B',
      navbarBg: '#92400E',
      navbarText: '#FEF3C7',
      fieldBorder: '#FDE68A',
      fieldBg: '#FFFBEB',
      bodyBg: '#FFFDF5',
      textPrimary: '#78350F',
    },
    fontFamily: 'system',
    buttonRadius: 8,
  },
  {
    key: 'royal-purple',
    name: 'Royal Purple',
    swatch: '#6D28D9',
    colors: {
      primary: '#6D28D9',
      secondary: '#A78BFA',
      navbarBg: '#4C1D95',
      navbarText: '#EDE9FE',
      fieldBorder: '#DDD6FE',
      fieldBg: '#ffffff',
      bodyBg: '#FAF5FF',
      textPrimary: '#3B0764',
    },
    fontFamily: 'system',
    buttonRadius: 6,
  },
];

export const FONT_OPTIONS = [
  { value: 'system', label: 'System Default' },
  { value: 'inter', label: 'Inter' },
  { value: 'roboto', label: 'Roboto' },
  { value: 'poppins', label: 'Poppins' },
  { value: 'opensans', label: 'Open Sans' },
];

const FONT_STACKS = {
  system: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  inter: '"Inter", sans-serif',
  roboto: '"Roboto", sans-serif',
  poppins: '"Poppins", sans-serif',
  opensans: '"Open Sans", sans-serif',
};

function getThemesRaw() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getThemes() {
  const stored = getThemesRaw();
  if (!stored || stored.length === 0) {
    const defaults = [DEFAULT_THEME];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(defaults));
    localStorage.setItem(ACTIVE_KEY, 'default');
    return defaults;
  }
  return stored;
}

export function saveTheme(theme) {
  const themes = getThemes();
  const idx = themes.findIndex((t) => t.id === theme.id);
  if (idx >= 0) {
    themes[idx] = theme;
  } else {
    themes.push(theme);
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(themes));
  return themes;
}

export function deleteTheme(id) {
  const themes = getThemes().filter((t) => t.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(themes));
  return themes;
}

export function setActiveTheme(id) {
  const themes = getThemes().map((t) => ({ ...t, isActive: t.id === id }));
  localStorage.setItem(STORAGE_KEY, JSON.stringify(themes));
  localStorage.setItem(ACTIVE_KEY, id);
  const active = themes.find((t) => t.id === id);
  if (active) applyTheme(active);
  return themes;
}

export function applyTheme(theme) {
  const root = document.documentElement;
  const { colors, fontFamily, buttonRadius } = theme;

  // CSS custom properties (used by the Manage Themes module itself and any future var() references)
  root.style.setProperty('--theme-primary', colors.primary);
  root.style.setProperty('--theme-secondary', colors.secondary);
  root.style.setProperty('--theme-navbar-bg', colors.navbarBg);
  root.style.setProperty('--theme-navbar-text', colors.navbarText);
  root.style.setProperty('--theme-field-border', colors.fieldBorder);
  root.style.setProperty('--theme-field-bg', colors.fieldBg);
  root.style.setProperty('--theme-body-bg', colors.bodyBg);
  root.style.setProperty('--theme-text-primary', colors.textPrimary);
  root.style.setProperty('--theme-font-family', FONT_STACKS[fontFamily] || FONT_STACKS.system);
  root.style.setProperty('--theme-btn-radius', `${buttonRadius}px`);

  const p = colors.primary;
  const s = colors.secondary;
  const navBg = colors.navbarBg;
  const navText = colors.navbarText;
  const fieldBorder = colors.fieldBorder;
  const fieldBg = colors.fieldBg;
  const bodyBg = colors.bodyBg;
  const textPrimary = colors.textPrimary;
  const font = FONT_STACKS[fontFamily] || FONT_STACKS.system;
  const radius = `${buttonRadius}px`;

  let styleEl = document.getElementById('dms-theme-override');
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'dms-theme-override';
    document.head.appendChild(styleEl);
  }

  styleEl.textContent = `
    /* ── Font & body ── */
    body, #root, .ant-layout { font-family: ${font} !important; }
    body, html { background-color: ${bodyBg} !important; color: ${textPrimary} !important; }
    .ant-layout, .ant-layout-content, .atbd-main-layout { background-color: ${bodyBg} !important; }

    /* ── Navbar / Header ── */
    .ant-layout-header > div { background-color: ${navBg} !important; }
    .ant-layout-header .text-white { color: ${navText} !important; }
    .ant-layout-header svg { color: ${navText} !important; }

    /* ── bg-primary: navbar gets navBg (more specific), everything else gets primary ── */
    .bg-primary { background-color: ${p} !important; }
    .ant-layout-header .bg-primary { background-color: ${navBg} !important; }

    /* ── Tailwind primary color utilities ── */
    .text-primary { color: ${p} !important; }
    .border-primary { border-color: ${p} !important; }
    .hover\\:text-primary:hover { color: ${p} !important; }
    .hover\\:bg-primary:hover { background-color: ${p} !important; }
    .hover\\:border-primary:hover { border-color: ${p} !important; }
    .focus\\:bg-primary:focus { background-color: ${p} !important; }
    .active\\:bg-primary:active { background-color: ${p} !important; }
    .bg-primary-transparent { background-color: ${p}18 !important; }
    .dark\\:border-primary { border-color: ${p} !important; }
    .dark\\:text-primary { color: ${p} !important; }

    /* ── Ant Design Buttons ── */
    .ant-btn-primary {
      background-color: ${p} !important;
      border-color: ${p} !important;
      border-radius: ${radius} !important;
    }
    .ant-btn-primary:not([disabled]):hover,
    .ant-btn-primary:not([disabled]):focus {
      background-color: ${s} !important;
      border-color: ${s} !important;
    }
    .ant-btn:not(.ant-btn-primary):not(.ant-btn-dangerous):not(.ant-btn-link) {
      border-radius: ${radius} !important;
    }
    .ant-btn-link { color: ${p} !important; }
    .ant-btn-link:hover { color: ${s} !important; }
    .ant-btn-default:hover { border-color: ${p} !important; color: ${p} !important; }

    /* ── Ant Design Menu ── */
    .ant-menu-item-selected,
    .ant-menu-item-selected > a,
    .ant-menu-item-selected span { color: ${p} !important; }
    .ant-menu:not(.ant-menu-horizontal) .ant-menu-item-selected {
      background-color: ${p}18 !important;
    }
    .ant-menu-item:hover,
    .ant-menu-item:hover > a,
    .ant-menu-submenu-title:hover { color: ${p} !important; }
    .ant-menu-submenu-selected > .ant-menu-submenu-title { color: ${p} !important; }

    /* ── Ant Design Inputs ── */
    .ant-input,
    .ant-input-affix-wrapper {
      border-color: ${fieldBorder} !important;
      background-color: ${fieldBg} !important;
      border-radius: ${radius} !important;
    }
    .ant-input:focus,
    .ant-input-focused,
    .ant-input-affix-wrapper:focus,
    .ant-input-affix-wrapper-focused {
      border-color: ${p} !important;
      box-shadow: 0 0 0 2px ${p}20 !important;
    }
    .ant-select-selector {
      border-color: ${fieldBorder} !important;
      background-color: ${fieldBg} !important;
      border-radius: ${radius} !important;
    }
    .ant-select-focused .ant-select-selector,
    .ant-select-open .ant-select-selector {
      border-color: ${p} !important;
      box-shadow: 0 0 0 2px ${p}20 !important;
    }
    .ant-picker {
      border-color: ${fieldBorder} !important;
      background-color: ${fieldBg} !important;
      border-radius: ${radius} !important;
    }
    .ant-picker-focused {
      border-color: ${p} !important;
      box-shadow: 0 0 0 2px ${p}20 !important;
    }
    .ant-input-number {
      border-color: ${fieldBorder} !important;
      border-radius: ${radius} !important;
    }

    /* ── Ant Design Tabs ── */
    .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn { color: ${p} !important; }
    .ant-tabs-ink-bar { background-color: ${p} !important; }
    .ant-tabs-tab:hover { color: ${p} !important; }

    /* ── Ant Design Pagination ── */
    .ant-pagination-item-active { border-color: ${p} !important; }
    .ant-pagination-item-active a { color: ${p} !important; }
    .ant-pagination-item:hover { border-color: ${p} !important; }
    .ant-pagination-item:hover a { color: ${p} !important; }

    /* ── Ant Design Checkbox & Radio ── */
    .ant-checkbox-checked .ant-checkbox-inner {
      background-color: ${p} !important;
      border-color: ${p} !important;
    }
    .ant-checkbox:hover .ant-checkbox-inner { border-color: ${p} !important; }
    .ant-radio-checked .ant-radio-inner { border-color: ${p} !important; }
    .ant-radio-inner::after { background-color: ${p} !important; }
    .ant-radio:hover .ant-radio-inner { border-color: ${p} !important; }

    /* ── Ant Design Switch ── */
    .ant-switch-checked { background-color: ${p} !important; }

    /* ── Ant Design Spinner ── */
    .ant-spin-dot-item { background-color: ${p} !important; }

    /* ── Ant Design Card ── */
    .ant-card { border-radius: ${radius} !important; }

    /* ── Links ── */
    a:not(.ant-btn):not(.ant-menu-item) { color: ${p}; }
    a:not(.ant-btn):not(.ant-menu-item):hover { color: ${s}; }
  `;
}

export function loadActiveTheme() {
  const storedVersion = parseInt(localStorage.getItem(THEME_VERSION_KEY) || '1', 10);

  if (storedVersion < THEME_VERSION) {
    // Reset the built-in default theme to the current DEFAULT_THEME definition
    const themes = getThemes().map((t) => (t.id === 'default' ? { ...DEFAULT_THEME } : t));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(themes));
    localStorage.setItem(THEME_VERSION_KEY, String(THEME_VERSION));
    const active = themes.find((t) => t.isActive);
    if (active) applyTheme(active);
    return;
  }

  const themes = getThemes();
  const active = themes.find((t) => t.isActive);
  if (active) applyTheme(active);
}

export function generateId() {
  return `theme_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}
